import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {v4 as uuidv4} from 'uuid';

import {BenchlingAppSessionMessageStyle} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import BenchChip from 'ui-library/chips/BenchChip';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import LoadingChip from 'ui-library/chips/LoadingChip';
import NoAccessChip from 'ui-library/chips/NoAccessChip';
import APIUtil from 'util/APIUtil';
import {assertNever} from 'util/Types';

import {GetCanvas} from '../canvases/__generated/GetCanvas';
import {GetBlob, GetBlobVariables} from './__generated/GetBlob';

type SessionMessageNode = NonNullable<
  ExtractByTypename<GetCanvas['benchlingAppCanvas'], 'BenchlingAppCanvasNode'>['session']
>['messages'][number];

export const BLOB_QUERY = gql`
  query GetBlob($id: StrictString!) @skipAudit @engComponent(name: BENCHLING_APPS) {
    resultBlob(id: $id) {
      ... on ResultBlobNode {
        id
        name
        url
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const CHIP_SIZE = ChipSize.MINI;
const CHIP_PREFIX_LENGTH = 4; // length of "{id:"
enum StatusMessageContentType {
  TEXT = 'TEXT',
  API_ID = 'API_ID',
  WAREHOUSE_ID = 'WAREHOUSE_ID',
}
interface StatusMessageContent {
  type: StatusMessageContentType;
  value: string;
}

// Used to temporarily substitute escaped brackets
function replaceAll(input: string, search: string, replacement: string): string {
  return input.replace(new RegExp(search, 'g'), replacement);
}

function elementClosure(element: JSX.Element): () => JSX.Element {
  return () => element;
}

function renderBlobChipFromQuery(): (data: GetBlob) => React.ReactNode {
  return (data) =>
    isInaccessible(data.resultBlob) ? (
      <NoAccessChip size={CHIP_SIZE} />
    ) : (
      <Chip size={CHIP_SIZE} text={data.resultBlob.name} href={data.resultBlob.url} />
    );
}

// A string may contain embedded chips, such as
//    "Transfer 4ml of {id:seq_okGUw9M1} into {id:con_0yoNDhM7}"
//
// renderChips breaks this up into non-chip substrings alternating with embedded chips,
// and returns them in a DIV.
// Brackets can be escaped with backslash ('\{') to avoid being recognized in chips.
export function renderChips(content: string): JSX.Element {
  // replace all '\{' with '<UUID>'
  const uniqueToken = `<${uuidv4()}>`;
  content = replaceAll(content, '\\\\{', uniqueToken);

  const statusMessageContents: Array<StatusMessageContent> = [];
  const regex = /{id:([\w-]+)}/g;
  let match: RegExpExecArray | null;
  let pos = 0; // string offset into content

  // eslint-disable-next-line no-cond-assign
  while ((match = regex.exec(content)) != null) {
    // Find the next chip
    if (match.index !== pos) {
      // Get the string preceding the chip. Restore escaped brackets, if any.
      const newString = replaceAll(content.substring(pos, match.index), uniqueToken, '{');
      statusMessageContents.push({type: StatusMessageContentType.TEXT, value: newString});
      pos = match.index;
    }
    // Get the chip's ID
    const idStart = match.index + CHIP_PREFIX_LENGTH;
    const idEnd = idStart + match[1].length;
    const id = content.substring(idStart, idEnd);
    const contentType =
      APIUtil.apiIdToMaybeType(id) != null
        ? StatusMessageContentType.API_ID
        : StatusMessageContentType.WAREHOUSE_ID;
    statusMessageContents.push({type: contentType, value: id});
    pos = match.index + match[0].length; // end of matched chip
  }
  if (pos < content.length) {
    // Add the last substring after all chips have been added
    const newString = replaceAll(content.substring(pos), uniqueToken, '{');
    statusMessageContents.push({type: StatusMessageContentType.TEXT, value: newString});
  }

  // Render the chips and text
  const result: Array<JSX.Element> = [];
  let key = 0;
  for (const content of statusMessageContents) {
    // eslint-disable-next-line default-case
    switch (content.type) {
      case StatusMessageContentType.TEXT:
        result.push(<span key={key}>{content.value}</span>);
        break;
      case StatusMessageContentType.API_ID:
        result.push(
          <BenchChip key={key} id={content.value} size={CHIP_SIZE} className={css(styles.bannerChip)} />
        );
        break;
      case StatusMessageContentType.WAREHOUSE_ID:
        result.push(
          <BenchlingQuery<GetBlob, GetBlobVariables>
            key={key}
            query={BLOB_QUERY}
            variables={{id: content.value}}
            renderLoading={elementClosure(<LoadingChip size={CHIP_SIZE} />)}
          >
            {renderBlobChipFromQuery()}
          </BenchlingQuery>
        );
        break;
    }
    key += 1;
  }

  return <>{result}</>;
}

export function sessionStatusToBannerProps(message: SessionMessageNode): InlineBannerProps {
  let type: InlineBannerType;
  let hideIcon = false;
  switch (message.style) {
    case BenchlingAppSessionMessageStyle.NONE:
      hideIcon = true;
      type = InlineBannerType.INFO;
      break;
    case BenchlingAppSessionMessageStyle.INFO:
      type = InlineBannerType.INFO;
      break;
    case BenchlingAppSessionMessageStyle.WARNING:
      type = InlineBannerType.WARNING;
      break;
    case BenchlingAppSessionMessageStyle.SUCCESS:
      type = InlineBannerType.SUCCESS;
      break;
    case BenchlingAppSessionMessageStyle.ERROR:
      type = InlineBannerType.ERROR;
      break;
    default:
      throw new Error(`Unexpected SessionMessageStyle: ${assertNever(message.style)}`);
  }
  return {bannerType: type, hideIcon, text: renderChips(message.content)};
}

const styles = StyleSheet.create({
  bannerChip: {
    // override "margin-right: 8px;" defined in less/components/chips.less
    marginRight: '0px',
  },
});
